import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { GroupItem, CreateGroupPayload, InviteGroupMemberPayload } from './types';

/**
 * Hook to fetch available organizational groups from GET /api/v2/groups/
 */
export function useGroups() {
  return useQuery<GroupItem[]>({
    queryKey: ['groups'],
    queryFn: async () => {
      const response = await api.get<{ results: GroupItem[] } | GroupItem[]>('/v2/groups/');
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return response.data?.results ?? [];
    },
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

/**
 * Hook to create a new sharing group with optional permanent sharing (POST /api/v2/groups/)
 */
export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation<GroupItem, Error, CreateGroupPayload>({
    mutationFn: async (payload) => {
      const res = await api.post<GroupItem>('/v2/groups/', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

/**
 * Hook to invite a member directly into a group (POST /api/v2/groups/{gid}/invite/)
 */
export function useInviteGroupMember() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { groupId: string; payload: InviteGroupMemberPayload }>({
    mutationFn: async ({ groupId, payload }) => {
      const res = await api.post<{ success: boolean }>(`/v2/groups/${groupId}/invite/`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}
